import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography } from "@mui/material";
import * as XLSX from "xlsx";

const MaternityLeaveRequestsByDistrictTable = forwardRef(
  ({ data, sortBy = "entityName" }, ref) => {
    useImperativeHandle(ref, () => ({
      exportToExcel,
    }));

    const processedData = useMemo(() => {
      if (
        !data?.maternityLeaveRequestsByDistrict ||
        !data?.maternityLeaveReplacingStaffRequestsByDistrict
      ) {
        return null;
      }

      const requestsData = data.maternityLeaveRequestsByDistrict;
      const replacingStaffData =
        data.maternityLeaveReplacingStaffRequestsByDistrict;

      const entityType =
        requestsData.length > 0 ? requestsData[0].entityType : "DISTRICT";
      const entityHeader =
        entityType === "SECTOR"
          ? "Sector"
          : entityType === "SCHOOL"
          ? "School"
          : "District";

      const combinedData = requestsData.map((request) => {
        const replacingStaff = replacingStaffData.find(
          (staff) => staff.entityId === request.entityId
        );

        return {
          ...request,
          replacingStaff: replacingStaff || {
            ApprovedByDDE_A0: 0,
            ApprovedByDDE_A1: 0,
            ApprovedByDDE_A2: 0,
            ApprovedByDDE_Total: 0,
            PendingApproval_A0: 0,
            PendingApproval_A1: 0,
            PendingApproval_A2: 0,
            PendingApproval_Total: 0,
            Total_A0: 0,
            Total_A1: 0,
            Total_A2: 0,
            Total_Requests: 0,
          },
        };
      });

      // Calculate totals for "All districts" row
      const totals = combinedData.reduce(
        (acc, item) => {
          acc.ApprovedByHT_A0 += item.ApprovedByHT_A0 || 0;
          acc.ApprovedByHT_A1 += item.ApprovedByHT_A1 || 0;
          acc.ApprovedByHT_A2 += item.ApprovedByHT_A2 || 0;
          acc.ApprovedByHT_Total += item.ApprovedByHT_Total || 0;
          acc.PendingApproval_A0 += item.PendingApproval_A0 || 0;
          acc.PendingApproval_A1 += item.PendingApproval_A1 || 0;
          acc.PendingApproval_A2 += item.PendingApproval_A2 || 0;
          acc.PendingApproval_Total += item.PendingApproval_Total || 0;
          acc.Total_A0 += item.Total_A0 || 0;
          acc.Total_A1 += item.Total_A1 || 0;
          acc.Total_A2 += item.Total_A2 || 0;
          acc.Total_Requests += item.Total_Requests || 0;
          acc.ApprovedByDDE_A0 += item.ApprovedByDDE_A0 || 0;
          acc.ApprovedByDDE_A1 += item.ApprovedByDDE_A1 || 0;
          acc.ApprovedByDDE_A2 += item.ApprovedByDDE_A2 || 0;
          acc.ApprovedByDDE_Total += item.ApprovedByDDE_Total || 0;
          acc.PendingReplacementApproval_A0 +=
            item.PendingReplacementApproval_A0 || 0;
          acc.PendingReplacementApproval_A1 +=
            item.PendingReplacementApproval_A1 || 0;
          acc.PendingReplacementApproval_A2 +=
            item.PendingReplacementApproval_A2 || 0;
          acc.PendingReplacementApproval_Total +=
            item.PendingReplacementApproval_Total || 0;
          acc.PlacedCandidates_A0 += item.PlacedCandidates_A0 || 0;
          acc.PlacedCandidates_A1 += item.PlacedCandidates_A1 || 0;
          acc.PlacedCandidates_A2 += item.PlacedCandidates_A2 || 0;
          acc.PlacedCandidates_Total += item.PlacedCandidates_Total || 0;

          // Replacing staff totals
          acc.replacingStaff.ApprovedByDDE_A0 +=
            item.replacingStaff.ApprovedByDDE_A0 || 0;
          acc.replacingStaff.ApprovedByDDE_A1 +=
            item.replacingStaff.ApprovedByDDE_A1 || 0;
          acc.replacingStaff.ApprovedByDDE_A2 +=
            item.replacingStaff.ApprovedByDDE_A2 || 0;
          acc.replacingStaff.ApprovedByDDE_Total +=
            item.replacingStaff.ApprovedByDDE_Total || 0;
          acc.replacingStaff.PendingApproval_A0 +=
            item.replacingStaff.PendingApproval_A0 || 0;
          acc.replacingStaff.PendingApproval_A1 +=
            item.replacingStaff.PendingApproval_A1 || 0;
          acc.replacingStaff.PendingApproval_A2 +=
            item.replacingStaff.PendingApproval_A2 || 0;
          acc.replacingStaff.PendingApproval_Total +=
            item.replacingStaff.PendingApproval_Total || 0;
          acc.replacingStaff.Total_A0 += item.replacingStaff.Total_A0 || 0;
          acc.replacingStaff.Total_A1 += item.replacingStaff.Total_A1 || 0;
          acc.replacingStaff.Total_A2 += item.replacingStaff.Total_A2 || 0;
          acc.replacingStaff.Total_Requests +=
            item.replacingStaff.Total_Requests || 0;

          return acc;
        },
        {
          entityName: "All requests",
          ApprovedByHT_A0: 0,
          ApprovedByHT_A1: 0,
          ApprovedByHT_A2: 0,
          ApprovedByHT_Total: 0,
          PendingApproval_A0: 0,
          PendingApproval_A1: 0,
          PendingApproval_A2: 0,
          PendingApproval_Total: 0,
          Total_A0: 0,
          Total_A1: 0,
          Total_A2: 0,
          Total_Requests: 0,
          ApprovedByDDE_A0: 0,
          ApprovedByDDE_A1: 0,
          ApprovedByDDE_A2: 0,
          ApprovedByDDE_Total: 0,
          PendingReplacementApproval_A0: 0,
          PendingReplacementApproval_A1: 0,
          PendingReplacementApproval_A2: 0,
          PendingReplacementApproval_Total: 0,
          PlacedCandidates_A0: 0,
          PlacedCandidates_A1: 0,
          PlacedCandidates_A2: 0,
          PlacedCandidates_Total: 0,
          replacingStaff: {
            ApprovedByDDE_A0: 0,
            ApprovedByDDE_A1: 0,
            ApprovedByDDE_A2: 0,
            ApprovedByDDE_Total: 0,
            PendingApproval_A0: 0,
            PendingApproval_A1: 0,
            PendingApproval_A2: 0,
            PendingApproval_Total: 0,
            Total_A0: 0,
            Total_A1: 0,
            Total_A2: 0,
            Total_Requests: 0,
          },
        }
      );

      return { data: [...combinedData, totals], entityHeader };
    }, [data]);

    const filteredData = useMemo(() => {
      if (!processedData?.data) return [];

      let filtered = [...processedData.data];

      filtered.sort((a, b) => {
        if (a.entityName === "All requests") return 1;
        if (b.entityName === "All requests") return -1;

        if (sortBy === "entityName") {
          return a.entityName.localeCompare(b.entityName);
        } else if (sortBy === "entityName_desc") {
          return b.entityName.localeCompare(a.entityName);
        } else if (sortBy === "totalRequests") {
          return (a.Total_Requests || 0) - (b.Total_Requests || 0);
        } else if (sortBy === "totalRequests_desc") {
          return (b.Total_Requests || 0) - (a.Total_Requests || 0);
        }
        return 0;
      });

      return filtered;
    }, [processedData, sortBy]);

    const exportToExcel = () => {
      if (!processedData?.data || processedData.data.length === 0) return;

      const wb = XLSX.utils.book_new();
      const excelData = [];

      const entityHeader = processedData.entityHeader || "District";

      const headerRow1 = [
        entityHeader,
        "Maternity Leave requests",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Waiting to be placed",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Placed candidates",
        "",
        "",
        "",
      ];

      const headerRow2 = [
        "",
        "Approved by HT",
        "",
        "",
        "",
        "Pending for approval",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
        "Approved by DDE",
        "",
        "",
        "",
        "Pending for approval",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
      ];

      const headerRow3 = [
        "",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
      ];

      excelData.push(headerRow1);
      excelData.push(headerRow2);
      excelData.push(headerRow3);

      filteredData.forEach((item) => {
        if (item.entityName !== "All requests") {
          const row = [
            item.entityName,
            item.ApprovedByHT_A0 || 0,
            item.ApprovedByHT_A1 || 0,
            item.ApprovedByHT_A2 || 0,
            item.ApprovedByHT_Total || 0,
            item.PendingApproval_A0 || 0,
            item.PendingApproval_A1 || 0,
            item.PendingApproval_A2 || 0,
            item.PendingApproval_Total || 0,
            item.Total_A0 || 0,
            item.Total_A1 || 0,
            item.Total_A2 || 0,
            item.Total_Requests || 0,
            item.replacingStaff.ApprovedByDDE_A0 || 0,
            item.replacingStaff.ApprovedByDDE_A1 || 0,
            item.replacingStaff.ApprovedByDDE_A2 || 0,
            item.replacingStaff.ApprovedByDDE_Total || 0,
            item.replacingStaff.PendingApproval_A0 || 0,
            item.replacingStaff.PendingApproval_A1 || 0,
            item.replacingStaff.PendingApproval_A2 || 0,
            item.replacingStaff.PendingApproval_Total || 0,
            (item.replacingStaff.ApprovedByDDE_A0 || 0) +
              (item.replacingStaff.PendingApproval_A0 || 0),
            (item.replacingStaff.ApprovedByDDE_A1 || 0) +
              (item.replacingStaff.PendingApproval_A1 || 0),
            (item.replacingStaff.ApprovedByDDE_A2 || 0) +
              (item.replacingStaff.PendingApproval_A2 || 0),
            (item.replacingStaff.ApprovedByDDE_Total || 0) +
              (item.replacingStaff.PendingApproval_Total || 0),
            item.PlacedCandidates_A0 || 0,
            item.PlacedCandidates_A1 || 0,
            item.PlacedCandidates_A2 || 0,
            item.PlacedCandidates_Total || 0,
          ];
          excelData.push(row);
        }
      });

      const totalsItem = filteredData.find(
        (item) => item.entityName === "All requests"
      );
      if (totalsItem) {
        const totalsRow = [
          "All requests",
          totalsItem.ApprovedByHT_A0 || 0,
          totalsItem.ApprovedByHT_A1 || 0,
          totalsItem.ApprovedByHT_A2 || 0,
          totalsItem.ApprovedByHT_Total || 0,
          totalsItem.PendingApproval_A0 || 0,
          totalsItem.PendingApproval_A1 || 0,
          totalsItem.PendingApproval_A2 || 0,
          totalsItem.PendingApproval_Total || 0,
          totalsItem.Total_A0 || 0,
          totalsItem.Total_A1 || 0,
          totalsItem.Total_A2 || 0,
          totalsItem.Total_Requests || 0,
          totalsItem.replacingStaff.ApprovedByDDE_A0 || 0,
          totalsItem.replacingStaff.ApprovedByDDE_A1 || 0,
          totalsItem.replacingStaff.ApprovedByDDE_A2 || 0,
          totalsItem.replacingStaff.ApprovedByDDE_Total || 0,
          totalsItem.replacingStaff.PendingApproval_A0 || 0,
          totalsItem.replacingStaff.PendingApproval_A1 || 0,
          totalsItem.replacingStaff.PendingApproval_A2 || 0,
          totalsItem.replacingStaff.PendingApproval_Total || 0,
          (totalsItem.replacingStaff.ApprovedByDDE_A0 || 0) +
            (totalsItem.replacingStaff.PendingApproval_A0 || 0),
          (totalsItem.replacingStaff.ApprovedByDDE_A1 || 0) +
            (totalsItem.replacingStaff.PendingApproval_A1 || 0),
          (totalsItem.replacingStaff.ApprovedByDDE_A2 || 0) +
            (totalsItem.replacingStaff.PendingApproval_A2 || 0),
          (totalsItem.replacingStaff.ApprovedByDDE_Total || 0) +
            (totalsItem.replacingStaff.PendingApproval_Total || 0),
          totalsItem.PlacedCandidates_A0 || 0,
          totalsItem.PlacedCandidates_A1 || 0,
          totalsItem.PlacedCandidates_A2 || 0,
          totalsItem.PlacedCandidates_Total || 0,
        ];
        excelData.push(totalsRow);
      }

      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      const wscols = [
        { wch: 15 }, // District
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 10 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 10 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 12 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 10 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 10 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 12 }, // Total
        { wch: 8 }, // A0
        { wch: 8 }, // A1
        { wch: 8 }, // A2
        { wch: 10 }, // Total
      ];
      ws["!cols"] = wscols;

      // Add borders and styling
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = { c: C, r: R };
          const cell_ref = XLSX.utils.encode_cell(cell_address);
          if (!ws[cell_ref]) continue;
          ws[cell_ref].s = {
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
      }

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        `Maternity Leave By ${entityHeader}s`
      );

      const date = new Date().toISOString().split("T")[0];
      const filename = `Maternity Leave Request By ${entityHeader} As of ${date}.xlsx`;

      XLSX.writeFile(
        wb,
        filename
      );
    };

    if (!processedData?.data || processedData.data.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.875rem",
              minWidth: "1400px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f8ff" }}>
                <th
                  rowSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: "bold",
                    verticalAlign: "middle",
                    backgroundColor: "#e6f3ff",
                    minWidth: "120px",
                  }}
                >
                  {processedData?.entityHeader || "District"}
                </th>
                <th
                  colSpan={12}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Maternity Leave requests
                </th>
                <th
                  colSpan={12}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Waiting to be placed
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Placed candidates
                </th>
              </tr>

              <tr style={{ backgroundColor: "#e6f3ff" }}>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Approved by HT
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Pending for approval
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Approved by DDE
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Pending for approval
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
              </tr>

              <tr style={{ backgroundColor: "#e6f3ff" }}>
                {/* Maternity Leave requests columns */}
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                {/* Placed candidates columns */}
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((item, index) => {
                const isTotal = item.entityName === "All requests";
                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: isTotal ? "#015198" : "white",
                      fontWeight: isTotal ? "bold" : "normal",
                      color: isTotal ? "white" : "black",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: isTotal ? "center" : "left",
                        fontSize: "0.85rem",
                        fontWeight: isTotal ? "bold" : "normal",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {item.entityName}
                    </td>

                    {/* Maternity Leave requests - Approved by HT */}
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.ApprovedByHT_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.ApprovedByHT_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.ApprovedByHT_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.ApprovedByHT_Total || 0).toLocaleString()}
                    </td>

                    {/* Maternity Leave requests - Pending approval */}
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PendingApproval_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PendingApproval_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PendingApproval_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PendingApproval_Total || 0).toLocaleString()}
                    </td>

                    {/* Maternity Leave requests - Total */}
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.Total_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.Total_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.Total_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal
                          ? "rgb(237, 108, 2)"
                          : "#f8f9fa",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.Total_Requests || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A0 || 0) +
                        (item.replacingStaff.PendingApproval_A0 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A1 || 0) +
                        (item.replacingStaff.PendingApproval_A1 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A2 || 0) +
                        (item.replacingStaff.PendingApproval_A2 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal
                          ? "rgb(237, 108, 2)"
                          : "#f8f9fa",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_Total || 0) +
                        (item.replacingStaff.PendingApproval_Total || 0)
                      ).toLocaleString()}
                    </td>

                    {/* Placed candidates */}
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: isTotal ? "bold" : "normal",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PlacedCandidates_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: isTotal ? "bold" : "normal",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PlacedCandidates_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: isTotal ? "bold" : "normal",
                        backgroundColor: isTotal ? "#015198" : "white",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PlacedCandidates_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: isTotal
                          ? "rgb(237, 108, 2)"
                          : "#f8f9fa",
                        color: isTotal ? "white" : "black",
                      }}
                    >
                      {(item.PlacedCandidates_Total || 0).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Box>
    );
  }
);

MaternityLeaveRequestsByDistrictTable.displayName =
  "MaternityLeaveRequestsByDistrictTable";

export default MaternityLeaveRequestsByDistrictTable;
